import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import CTASection from "@/components/CTASection";
import { useJsonLd } from "@/hooks/useJsonLd";
import { getService } from "@/data/site";
import { getLocation } from "@/data/areas";

const ServiceLocation = ({ area }) => {
  const loc = getLocation(area.city);
  const base = getService(area.baseService);

  useEffect(() => {
    document.title = `${area.keyword} — Click World Online`;
  }, [area]);

  useJsonLd(
    "faq-jsonld",
    area && {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: area.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }
  );

  return (
    <>
      <PageHeader
        eyebrow={`${loc.name} · Click World Online`}
        title={area.keyword}
        text={area.intro}
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: loc.name, to: `/locations/${loc.slug}` },
          { label: base.title },
        ]}
      />

      <section className="bg-navy-deep" data-testid="area-detail-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="The Local Angle" title={<>Built for how <span className="text-electric">{loc.name}</span> buys.</>} />
            <Reveal delay={0.1}>
              <p className="mt-6 text-slatec text-base md:text-lg leading-relaxed max-w-2xl">{area.angle}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <h2 className="mt-14 font-display text-2xl font-extrabold text-mist tracking-tight">What we focus on here</h2>
              <div className="mt-6 flex flex-col gap-4">
                {area.focus.map((f, i) => (
                  <div key={f} className="flex items-center gap-4 bg-navy-light border border-white/10 rounded-2xl px-6 py-4">
                    <span className="font-display text-sm font-bold text-electric">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-mist/85">{f}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-navy-light border border-white/10 rounded-3xl p-8 lg:p-10 grain relative overflow-hidden lg:sticky lg:top-28">
                <div className="absolute inset-0 dot-grid opacity-15" aria-hidden="true" />
                <div className="relative">
                  <h2 className="font-display text-xl font-extrabold text-mist tracking-tight">Keep exploring</h2>
                  <div className="mt-6 flex flex-col gap-3">
                    {base && (
                      <Link
                        to={`/services/${base.slug}`}
                        data-testid="area-base-service-link"
                        className="group flex items-center justify-between gap-3 border border-white/10 rounded-2xl px-5 py-4 hover:border-electric/40 transition-colors"
                      >
                        <span>
                          <span className="block text-xs text-mist/40 uppercase tracking-widest">Full service details</span>
                          <span className="block text-mist/90 font-medium mt-1">{base.title}</span>
                        </span>
                        <ArrowUpRight size={17} className="text-electric shrink-0" />
                      </Link>
                    )}
                    <Link
                      to={`/locations/${loc.slug}`}
                      data-testid="area-location-link"
                      className="group flex items-center justify-between gap-3 border border-white/10 rounded-2xl px-5 py-4 hover:border-electric/40 transition-colors"
                    >
                      <span>
                        <span className="block text-xs text-mist/40 uppercase tracking-widest">Everything we do in</span>
                        <span className="block text-mist/90 font-medium mt-1">{loc.name}</span>
                      </span>
                      <ArrowUpRight size={17} className="text-electric shrink-0" />
                    </Link>
                    <Link
                      to="/portfolio"
                      data-testid="area-work-link"
                      className="group flex items-center justify-between gap-3 border border-white/10 rounded-2xl px-5 py-4 hover:border-electric/40 transition-colors"
                    >
                      <span>
                        <span className="block text-xs text-mist/40 uppercase tracking-widest">Proof</span>
                        <span className="block text-mist/90 font-medium mt-1">Our work & 360° portfolio</span>
                      </span>
                      <ArrowUpRight size={17} className="text-electric shrink-0" />
                    </Link>
                  </div>
                  <Link
                    to="/contact"
                    data-testid="area-cta"
                    className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
                  >
                    Get a quote for your {loc.name} business <ArrowUpRight size={18} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-mist text-navy dot-grid-light" data-testid="area-faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionHeading dark={false} eyebrow="FAQs" title={<>Common questions from <span className="text-brand">{loc.name} businesses.</span></>} />
          <Reveal className="mt-12">
            <FaqAccordion items={area.faqs} idPrefix="area-faq" />
          </Reveal>
        </div>
      </section>

      <CTASection title={`Ready to grow in ${loc.name}?`} text={`Tell us about your ${loc.name} business — we'll recommend the sharpest starting point for ${base ? base.title.toLowerCase() : "digital growth"}.`} />
    </>
  );
};

export default ServiceLocation;
